import symbolManifest from './symbols.json' with { type: 'json' };
import { featureId, positionOf, teamFeatures, visibleTeams } from './session.js';

export const symbolUrl = (id) => symbolManifest[String(id || '')] || null;
export const normalizeSymbol = (id) => symbolUrl(id) ? String(id) : '';
const metersPerDegree = 111320;
const radians = Math.PI / 180;
export const distanceMeters = (a, b) => Math.hypot(
  (b[0] - a[0]) * Math.cos((a[1] + b[1]) / 2 * radians) * metersPerDegree,
  (b[1] - a[1]) * metersPerDegree
);
export const formatLength = (meters) => meters >= 1000 ? `${(meters / 1000).toFixed(2)} km` : `${meters.toFixed(1)} m`;
export const formatArea = (area, language = 'en') => area >= 1_000_000 ? `${(area / 1_000_000).toFixed(2)} km²`
  : `${Math.round(area).toLocaleString(language)} m²`;

export const polygonMetrics = (ring = []) => {
  if (ring.length < 3) return { area: 0, center: null };
  const latitude = ring.reduce((sum, point) => sum + point[1], 0) / ring.length;
  // Translate to the first point to avoid cancellation when computing small areas.
  const origin = ring[0], scaleX = metersPerDegree * Math.cos(latitude * radians);
  let twiceArea = 0, cx = 0, cy = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = [ring[i][0] - origin[0], ring[i][1] - origin[1]];
    const b = [ring[i + 1][0] - origin[0], ring[i + 1][1] - origin[1]];
    const cross = a[0] * b[1] - b[0] * a[1];
    twiceArea += cross; cx += (a[0] + b[0]) * cross; cy += (a[1] + b[1]) * cross;
  }
  const center = Math.abs(twiceArea) > 1e-12 ? [origin[0] + cx / (twiceArea * 3), origin[1] + cy / (twiceArea * 3)]
    : ring.reduce((sum, point) => [sum[0] + point[0] / ring.length, sum[1] + point[1] / ring.length], [0, 0]);
  return { area: Math.abs(twiceArea * scaleX * metersPerDegree) / 2, center };
};

export const featureMeasurement = (feature, language) => {
  const geometry = feature?.geometry;
  if (geometry?.type === 'Polygon' && feature.properties?.kind !== 'arrow') return formatArea(polygonMetrics(geometry.coordinates[0]).area, language);
  if (geometry?.type === 'LineString') return formatLength(geometry.coordinates.slice(1).reduce((sum, point, index) => sum + distanceMeters(geometry.coordinates[index], point), 0));
  return '';
};

export const buildFeatureLabels = (collection, session, language = 'en') => {
  const teamsByFeature = new Map();
  visibleTeams(session).forEach((team) => teamFeatures(team, session).forEach((feature, index) => {
    const id = featureId(feature, index);
    teamsByFeature.set(id, [...(teamsByFeature.get(id) || []), team.name]);
  }));
  const labels = [];
  const add = (feature, index, coordinates, text, labelType) => {
    if (!positionOf(coordinates) || !text) return;
    labels.push({ type: 'Feature', properties: {
      id: featureId(feature, index), label: text, labelType,
      poiIcon: normalizeSymbol(feature.properties.poiIcon), color: feature.properties.color
    }, geometry: { type: 'Point', coordinates } });
  };
  collection.features.forEach((feature, index) => {
    const p = feature.properties;
    if (p._featureLabelHidden || p.isLineEndpoint) return;
    const geometry = feature.geometry;
    const rawName = String(p.label || p.name || p.title || '').trim();
    const name = rawName.toLowerCase() === 'untitled' ? '' : rawName;
    if (geometry.type === 'Point') add(feature, index, geometry.coordinates, name, 'point');
    if (geometry.type === 'Polygon' && p.kind !== 'arrow') {
      const { center } = polygonMetrics(geometry.coordinates[0]);
      const text = [name, (teamsByFeature.get(featureId(feature, index)) || []).join(', '),
        p._featureSizeHidden ? '' : featureMeasurement(feature, language)].filter(Boolean).join('\n');
      add(feature, index, center, text, 'polygon');
    }
    if (geometry.type === 'LineString') {
      add(feature, index, geometry.coordinates[0], name, 'line-name');
      if (!p._featureSizeHidden) add(feature, index, geometry.coordinates.at(-1), featureMeasurement(feature, language), 'line-length');
    }
  });
  return { type: 'FeatureCollection', features: labels };
};
